import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error) => ReactNode);
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log rich error details for debugging
    console.error('[ERROR BOUNDARY] Uncaught error', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      location: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error as Error);
      }
      
      return this.props.fallback || (
        <div className="p-4 text-red-500 border border-red-200 rounded-md bg-red-50">
          <h2 className="text-lg font-medium">Something went wrong</h2>
          <p className="text-sm">{this.state.error?.message || 'Unknown error'}</p>
        </div>
      );
    }

    return this.props.children;
  }
}
